import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  FolderOpen, 
  TrendingUp, 
  Users, 
  Plus,
  ArrowRight,
  BarChart3
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiUtils } from '../utils/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    activeProducts: 0,
    loading: true
  });

  const [recentProducts, setRecentProducts] = useState([]);
  const [recentCategories, setRecentCategories] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const [productsResponse, categoriesResponse] = await Promise.all([
        apiUtils.fetchWithPagination('/products', { limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }),
        apiUtils.fetchWithPagination('/categories', { limit: 5, sortBy: 'createdAt', sortOrder: 'desc' })
      ]);

      if (productsResponse.success && categoriesResponse.success) {
        setStats({
          totalProducts: productsResponse.total,
          totalCategories: categoriesResponse.total,
          activeProducts: productsResponse.data.filter(p => p.isActive).length,
          loading: false
        });

        setRecentProducts(productsResponse.data);
        setRecentCategories(categoriesResponse.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: Package,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/products'
    },
    {
      title: 'Categories',
      value: stats.totalCategories,
      icon: FolderOpen,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/categories'
    },
    {
      title: 'Active Products',
      value: stats.activeProducts,
      icon: TrendingUp,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/products?isActive=true'
    },
    {
      title: 'Revenue',
      value: '$12.5K',
      icon: BarChart3,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      link: '#'
    }
  ];

  const quickActions = [
    {
      title: 'Add New Product',
      description: 'Create a new fruit product',
      icon: Package,
      link: '/products/new',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Add Category',
      description: 'Create a new category',
      icon: FolderOpen,
      link: '/categories/new',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  if (stats.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Welcome to A2Z Trading Admin Panel
          </p>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-brand-500 to-brand-600 rounded-2xl p-8 text-white">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-bold mb-4">Welcome to A2Z Trading!</h2>
          <p className="text-brand-100 text-lg leading-relaxed">
            Your comprehensive wholesale management system is ready. Use the navigation menu to manage your categories and products efficiently. 
            Start by organizing your inventory with categories, then add your products with detailed information and pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/categories"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:transform hover:scale-105"
            >
              Manage Categories
            </Link>
            <Link
              to="/products"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:transform hover:scale-105"
            >
              Manage Products
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;